import { queryOptions } from "@tanstack/react-query";
import type { MsTeamsConnectionService } from "@/services/msteams-connection.service";
import type { MsTeamsLinkService } from "@/services/msteams-link.service";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * Microsoft Teams, as two reads (NG-2813).
 *
 * **The workspace's connection** (plan §5a of NG-1096),
 * `GET {ws}/integrations/msteams/connection/`,
 * `[slug, uid, 'integrations', 'msteams']`. One row: a workspace consents to
 * one Entra tenant, and a workspace with none answers `state: "not_enabled"`.
 * Read by the Integration library and the Teams connector page.
 *
 * **Reference** (contract §2); the pages' loaders read it fresh on every visit
 * (`staleTime: 0`), as the store did, and the sign-in callback's return re-reads
 * it, since the callback created the row. A plain GET, so `"safe-read"`.
 *
 * **The signed-in person's own links** (plan §5a FR5–FR7, NG-1134),
 * `GET /api/users/me/integrations/msteams/links/`,
 * `['*', uid, 'msteams', 'links']`: one row per workspace of theirs that has
 * consented to Teams, in no one workspace, so it sits under `GLOBAL_TENANT`.
 * Read by the profile's Teams block, the notification settings' Teams column
 * and the bell's nudge. Read once and kept (`staleTime: Infinity`, never on
 * focus or reconnect), as the store's `ensureFetched` did: staleness is one
 * badge a state behind, and the block re-reads it on mount and on the sign-in
 * callback's return.
 *
 * Plain GETs, so `"safe-read"`.
 *
 * @writers `useTeamsConnectionWriters` (the Teams panel): enable, disable,
 * disconnect and the test card write each answer into the connection; disable
 * flips it first and puts it back on a failure. `useUserTeamsLinkWriters`:
 * unlink writes the whole payload it answers with into the links.
 */

/** The key. */
export function teamsConnectionKey(scope: QueryScope) {
  return scopedKey(scope, "integrations", "msteams");
}

/** The workspace's Teams connection. */
export function teamsConnectionQuery(
  service: Pick<MsTeamsConnectionService, "getConnection">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: teamsConnectionKey(scope),
    queryFn: scopedRead((request) => service.getConnection(scope.tenant, request), {
      retry: "safe-read",
    }),
  });
}

/** The links key; `scope` is the `GLOBAL_TENANT` one. */
export function userTeamsLinksKey(scope: QueryScope) {
  return scopedKey(scope, "msteams", "links");
}

/** The signed-in person's own Teams links, across their workspaces. */
export function userTeamsLinksQuery(
  service: Pick<MsTeamsLinkService, "getLinks">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryKey: userTeamsLinksKey(scope),
    queryFn: scopedRead((request) => service.getLinks(request), { retry: "safe-read" }),
  });
}
